import type {
  DirectReportStatus,
  HssStatusResponse,
} from "@/services/hardware-self-service/hardware-self-service.types";
import { ALIAS_PATTERN, LINKED_REPORT_QUERY_PARAM } from "./campaigns.constants";
import type { TeamView } from "./campaigns-deep-link.types";
import { needsManagerAttention } from "./campaigns.util";

export const parseLinkedReport = (search: string): string | undefined => {
  const alias = new URLSearchParams(search).get(LINKED_REPORT_QUERY_PARAM)?.trim().toLowerCase();
  return alias && ALIAS_PATTERN.test(alias) ? alias : undefined;
};

const isLinkedReport = (report: DirectReportStatus, linkedAlias: string): boolean =>
  report.username.toLowerCase() === linkedAlias && Boolean(report.status.campaign);

export const buildTeamView = (
  directReports: DirectReportStatus[],
  linkedAlias: string | undefined
): TeamView => {
  const matched =
    linkedAlias === undefined
      ? undefined
      : directReports.find((report) => isLinkedReport(report, linkedAlias));
  return {
    reports: directReports.filter((report) => report === matched || needsManagerAttention(report)),
    matchedUsername: matched?.username,
    unmatchedAlias: matched ? undefined : linkedAlias,
  };
};

/**
 * A link is kept only when it can land on My Team's Baselining: the alias is a direct report in an
 * active campaign, or the caller would have the team tab without the link. Never for their own alias.
 */
export const shouldDropLinkedReport = (
  linkedAlias: string,
  status: HssStatusResponse,
  currentUsername: string
): boolean => {
  if (linkedAlias === currentUsername.toLowerCase()) {
    return true;
  }
  const directReports = status.directReports ?? [];
  const isMatched = directReports.some((report) => isLinkedReport(report, linkedAlias));
  return !isMatched && !directReports.some(needsManagerAttention);
};
